/**
 * Syndicate Core - Pure logic for the Syndicate prestige layer (M3)
 *
 * No browser/Alpine dependencies: consumed by the UI wrapper AND the headless
 * simulator, same contract as command-core.js.
 *
 * Where Command prestiges a CHARACTER into marks, Syndicate prestiges an ERA
 * into Writs. Founding wipes the whole Command layer - marks, Legends,
 * upgrades, charters, vault, characters, credits - in exchange for Writs and
 * the charters they buy, which are the things that make each rebuilt era
 * faster than the last.
 *
 * See docs/layers/3-syndicate/design.md. The wipe/persist ledger in §2 is the
 * contract; tests/game/syndicate.test.js asserts BOTH columns, because a
 * prestige reset is equally broken if it keeps too much or too little.
 */

import * as config from '../constants.js';
import { gameState, defaultCommandState, defaultSyndicateState } from './gameState-core.js';
import { calculateCommandMarks, commandUpgradeCost, requiredUpgradesMissing, disburseInventory } from './command-core.js';
import { findItem } from './util.js';

/**
 * Promotions required before the first founding. Lands the L2 era at ~300h.
 *
 * BACK TO 15, the original design value, after the S7c pass established that
 * the two measurements which condemned it were both distorted:
 *
 *   1. The old "marks-rate peak" promotion policy could not end a tour before
 *      MIN_TOUR_DAYS + PEAK_PATIENCE, and that floor BOUND - across eight runs
 *      the shortest tour was 18.3h +/- 0.6, the constant itself. Tours averaged
 *      29h and 15 of them cost ~420h. Fixed by the replacement policy
 *      (sim-promotion.js); tours now run ~21.5h, uncensored.
 *   2. The Guild Vault upgrade taxed 1,040 marks for an effect that does not
 *      exist (see progressionScaling.vaultCarryover). Removing it freed the
 *      marks and started every recruit at the full carryover share.
 *
 * With both corrected, 15 x ~21.5h fits the budget it was always meant to.
 *
 * MEASURED, n=8 per row, replacement policy + focused play + vault removed:
 *
 *     legends   L2 era h         founding day        writs
 *        13     258.7 +/- 17.6   23.5 +/- 1.7  LOW   29.0 +/- 2.0
 *        15     314.7 +/- 28.8   28.3 +/- 2.1  OK    31.8 +/- 2.1  HIGH
 *
 * ⚠ WRITS NOW OVERSHOOT, and that is the BAND's problem, not this number's.
 * 26-30 was derived against an era banking ~700-900 marks. Removing the vault
 * lifted marks/tour to ~70, so 14 tours bank ~980 and sqrt gives 31. Nothing
 * between 13 and 15 satisfies both the day band and the Writ band - 14
 * interpolates to day 25.9 (just under) and 31 Writs (just over). Re-derive the
 * Writ band against the post-removal economy rather than contorting the
 * promotion count to hit a figure whose own basis has changed.
 *
 * ⚠ RE-DERIVING THIS. Era length has sd ~30-42h, so use n>=8 - a 3-run mean has
 * a standard error of ~24h and cannot resolve anything under ~50h. Two 3-run
 * samples of one build landed 58h apart during this pass and were briefly
 * mistaken for a regression. This number is also tied to the promotion POLICY
 * and the play style, not just the game data: both moved it this pass.
 */
export const FOUNDING_LEGEND_REQUIREMENT = 15;

/** A founding always needs a real roster, however much Memory is bought. */
export const FOUNDING_LEGEND_FLOOR = 8;

/**
 * Legends needed to found RIGHT NOW — the base, less Institutional Memory.
 *
 * ⚠ THIS IS THE ONLY LEVER ON ERA PACING, and S14 established that the hard
 * way. An era is `requirement x tour length`, and tour length cannot be moved
 * by making the player stronger: the promotion policy ends a tour when the
 * incumbent's marks rate drops below what a fresh recruit averages, and that
 * benchmark is measured from the player's own recent tours. Buff anything and
 * both sides rise together, so the crossover never moves. Measured three ways —
 * a 90% Command-upgrade discount, a charter reorder, and S13's 3.5x lift in
 * convoy credits — and era length did not budge from 19-20 days in any of them.
 *
 * So the compression a rebuilt era is supposed to have has to be bought
 * explicitly, and this is where. Measured against unchanged tour lengths, ranks
 * of 3/5/6 produce the curve the calibration targets ask for:
 *
 *     era 1   15 Legends   28.1d   (target 26-30)
 *     era 2   12 Legends   15.2d   (target 15-18)
 *     era 3   10 Legends   12.7d   (docs floor 11-13)
 *     era 4    9 Legends   11.4d   -> 4 foundings by day 67
 *
 * Floored at `FOUNDING_LEGEND_FLOOR` so no amount of Memory turns founding into
 * a formality — the roster is what a Syndicate IS, and a charter that erased it
 * would erase the layer.
 */
export function foundingLegendRequirement() {
    const relief = getCharterEffect('institutional-memory', 0);
    return Math.max(FOUNDING_LEGEND_FLOOR, FOUNDING_LEGEND_REQUIREMENT - relief);
}

/**
 * Writ yield coefficient. Sqrt damping is the point: doubling an era's marks
 * does NOT double its Writs, so camping one era to farm it is a losing play
 * against founding again. Growth is meant to come from founding MORE.
 */
export const WRIT_COEFFICIENT = 1.0;

export function ensureSyndicateState() {
    if (!gameState.syndicate) {
        gameState.syndicate = defaultSyndicateState();
    }
    // Backfill keys added after a save was written. The branch is created
    // wholesale above, so a save from before `stores` existed has the branch but
    // not the field — and promotion writes to it without a guard.
    if (!gameState.syndicate.consignment) gameState.syndicate.consignment = {};
    if (!gameState.syndicate.stores) gameState.syndicate.stores = {};
    if (!gameState.syndicate.demand) gameState.syndicate.demand = {};
    return gameState.syndicate;
}

/**
 * Writs issued for an era.
 * @param {number} eraMarks - marks BANKED this era (not the spendable balance)
 * @returns {number}
 */
export function writsForEra(eraMarks) {
    const marks = Number(eraMarks);
    if (!Number.isFinite(marks) || marks <= 0) return 0;
    return Math.floor(WRIT_COEFFICIENT * Math.sqrt(marks));
}

export function getCharterRank(charterId) {
    return gameState.syndicate?.upgrades?.[charterId] || 0;
}

/**
 * Effect value at the owned rank, or `fallback` when unowned.
 * Mirrors command-core's getUpgradeEffect so integration sites read alike.
 */
export function getCharterEffect(charterId, fallback = 0) {
    const rank = getCharterRank(charterId);
    if (rank <= 0) return fallback;
    const def = config.syndicateUpgrades?.[charterId];
    if (!def) return fallback;
    return def.effectValues?.[rank - 1] ?? fallback;
}

export function isSyndicateRevealed() {
    const syndicate = gameState.syndicate;
    if (!syndicate) return false;
    // Latches on, like Command's: once the player has seen the layer it stays
    // available between eras, which is exactly when charters get bought.
    if (syndicate.revealed) return true;
    if (syndicate.founded > 0 || (gameState.command?.legends?.length || 0) >= FOUNDING_LEGEND_REQUIREMENT) {
        syndicate.revealed = true;
        return true;
    }
    return false;
}

/**
 * Whether founding is permitted right now.
 *
 * Mirrors canPromoteCore's blockers (founding subsumes a promotion) and adds
 * the era requirement. Deliberately a BLOCKER check, not a "worth it" check.
 */
export function canFoundSyndicateCore() {
    const command = gameState.command;
    if (!command) return { ok: false, reason: 'no-command-layer' };

    const legends = command.legends?.length || 0;
    const need = foundingLegendRequirement();
    if (legends < need) {
        return { ok: false, reason: 'insufficient-legends', have: legends, need };
    }

    const character = gameState.character;
    if (character?.isMarketplace || character?.marketplaceData) {
        return { ok: false, reason: 'listed-on-marketplace' };
    }
    // Any owned raid state blocks it, same rule promotion and deletion apply.
    if (gameState.currentRaid?.raidOwnerId === gameState.activeCharacterId &&
        (gameState.currentRaid.currentRaidGrid || gameState.currentRaid.raidInProgress || gameState.currentRaid.raidCompleted)) {
        return { ok: false, reason: 'raid-in-progress' };
    }
    // In-flight operations reference era-Legends, who are about to stop existing.
    if ((gameState.syndicate?.operations?.length || 0) > 0) {
        return { ok: false, reason: 'operations-in-flight' };
    }
    return { ok: true };
}

/**
 * Elect the Syndicate Leader: the highest-marks Legend of the ending era.
 * Returns a snapshot, or null for an era with no Legends (not reachable
 * through founding, which requires 15, but callable independently).
 */
export function electLeaderCore(legends = gameState.command?.legends || []) {
    if (!legends.length) return null;
    const best = legends.reduce((a, b) => ((b.marksEarned || 0) > (a.marksEarned || 0) ? b : a));
    return {
        name: best.name,
        faction: best.faction,
        avatarSeed: best.avatarSeed,
        level: best.level,
        marksEarned: best.marksEarned || 0,
        promotedRole: best.promotedRole,
        electedAt: Date.now(),
    };
}

/**
 * Found a Syndicate: end the Command era, issue Writs, wipe the layer.
 *
 * Sequence matters and is asserted:
 *   1. elect the Leader from the era's Legends (before they are wiped)
 *   2. fold the ACTIVE character's unbanked marks into eraMarks
 *   3. compute Writs from the completed total
 *   4. apply the wipe
 *   5. re-grant what charters make permanent
 *
 * Step 2 is decision 8 in the spec: founding is irreversible, so a player who
 * founds without promoting their current character first must not silently
 * lose that character's marks. There is no "promote first!" footgun.
 *
 * @returns {{success: boolean, reason?: string, writs?: number, eraMarks?: number, leader?: object|null}}
 */
export function foundSyndicateCore() {
    const eligible = canFoundSyndicateCore();
    if (!eligible.ok) return { success: false, reason: eligible.reason };

    const syndicate = ensureSyndicateState();
    const command = gameState.command;

    // 1. Leader is elected from the era about to end.
    const leader = electLeaderCore(command.legends);
    if (syndicate.leader) syndicate.pastLeaders.push(syndicate.leader);

    // 2. Unbanked marks on the active character count toward the era. Same
    //    figure the promote path banks, so founding and promote-then-found are
    //    worth the same - which is the whole point of decision 8.
    let unbanked = 0;
    if (gameState.character) {
        unbanked = calculateCommandMarks(gameState.character)?.total || 0;
        syndicate.eraMarks += unbanked;
    }

    // 3. Writs price the completed era.
    const eraMarks = syndicate.eraMarks;
    const writs = writsForEra(eraMarks);

    // 4. The wipe. Blueprints survive for the same reason promotion carries
    //    them at 100%: they are the account-long artifact arc, and the 27-design
    //    unlock record (command.unlockedArtifacts) is explicitly account-level.
    const consignment = syndicate.consignment || (syndicate.consignment = {});

    //    Both the vault's contents and the active character's kit are routed by
    //    the SAME disburseInventory() promotion uses — founding and promotion
    //    are the same event to the economy (a character stops existing and their
    //    kit has to land somewhere), so they must not disagree about where any
    //    item type goes.
    //
    //    vaultShare 0 for both: there is no successor to inherit a share. That
    //    also means the vault's own materials consign rather than vanish —
    //    without it, promote-then-found loses the rank share while founding
    //    directly keeps it, exactly the footgun decision 8 exists to prevent,
    //    just on the material side.
    const survivingBlueprints = {};
    disburseInventory(command.vault, { vaultShare: 0, vault: survivingBlueprints, consignment });

    //    The active character's kit, on the same principle as the unbanked marks
    //    folded in at step 2: founding without promoting first must not silently
    //    cost you the character's stock.
    //
    //    Their relics and consumables are NOT cashed out. Founding resets credits
    //    to the levy floor a few lines below, so crediting them here would be a
    //    number the player watches appear and then vanish in the same click. The
    //    wipe ledger is explicit that the era's wealth does not survive.
    if (gameState.character) {
        disburseInventory(gameState.character.inventory, {
            vaultShare: 0, vault: survivingBlueprints, consignment,
        });
    }

    //    The consignment is DELIVERED: everything this era banked becomes the
    //    new Syndicate's dock, and the consignment starts empty for the era
    //    about to begin. This is the only thing that ever fills `stores`, which
    //    is what makes a dock a per-era budget rather than a running total.
    Object.entries(consignment).forEach(([itemId, qty]) => {
        if (qty > 0) syndicate.stores[itemId] = (syndicate.stores[itemId] || 0) + qty;
    });
    syndicate.consignment = {};

    syndicate.totalTours += Math.max(0, (command.tour || 1) - 1);
    syndicate.writs += writs;
    syndicate.eraMarks = 0;
    syndicate.founded += 1;
    syndicate.leader = leader;
    syndicate.revealed = true;

    const preserved = {
        unlockedArtifacts: command.unlockedArtifacts || [],
        revealed: command.revealed,
        grandfathered: command.grandfathered,
    };
    gameState.command = {
        ...defaultCommandState(),
        ...preserved,
        vault: survivingBlueprints,
    };

    // 5. Charters that reach back into the fresh era.
    //
    // GRANTED, NOT RETAINED, and deliberately so: reaching L3 is what automates
    // L1 permanently, so the charter hands over Auto-Raid Protocol whether or
    // not Command ever bought it. No check on prior ownership, because there is
    // nothing to check — a player who never bought it is exactly who this is
    // for. The Command upgrade remains worth its 300 marks as EARLY access
    // (~day 16 if you bank for it, against a first founding at ~day 25), not as
    // a prerequisite. See the note on `standing-orders` in syndicateUpgrades.js;
    // the charter's old wording said "retained", which made this read as a bug.
    if (getCharterRank('standing-orders') > 0) {
        gameState.command.upgrades['auto-raid-protocol'] = 1;
        // Auto-Fabricator comes with it, and must: a founding wipes Command
        // upgrades, so granting only the raid half would hand every post-
        // founding era a character that raids hands-off and never crafts —
        // which is the ~29%-of-marks hole this pair exists to close. Automation
        // that survives a founding has to survive it whole.
        gameState.command.upgrades['auto-fabricator'] = 1;
    }
    const endowment = getCharterEffect('founders-endowment', 0);
    if (endowment > 0) gameState.command.marks = endowment;

    // Characters and credits reset; the crafting multiplier follows tour=1 on
    // its own now that it is derived rather than compounded, so there is
    // nothing to restore here.
    gameState.characters = [];
    gameState.character = null;
    gameState.activeCharacterId = null;
    gameState.player.credits = config.progressionScaling.promotionCreditLevy.floor;

    return { success: true, writs, eraMarks, unbanked, leader };
}

// ---------------------------------------------------------------------------
// Succession Doctrine — automated promotion at the marks-rate peak
// ---------------------------------------------------------------------------

/**
 * Policy constants, ported from the simulator's measured promotion policy
 * (`game-simulator.js`, "marks/hour peak").
 *
 * The sim counts RAID-DAYS; this counts RAIDS. Wall-clock is ambiguous in a
 * browser — a tab left open overnight is not a long tour, it is an idle one —
 * whereas a raid is the same unit of progress in both. `peakPatienceRaids` is
 * the spec's ~6 raids ≈ the sim's 0.5 raid-days.
 *
 * ⚠ PROVISIONAL. These mirror the SHAPE of the measured sim policy, not
 * measured browser values; S7 calibrates them against auto-promote parity
 * (tour lengths within ±20% of the sim-native policy).
 */
export const SUCCESSION_POLICY = {
    minTourRaids: 8,        // don't promote a character who has barely deployed
    peakPatienceRaids: 6,   // rate must stay off its peak this long
    maxTourRaids: 60,       // deep-push safety valve
};

export function ensureSuccessionState() {
    const syndicate = ensureSyndicateState();
    if (!syndicate.succession) {
        syndicate.succession = defaultSyndicateState().succession;
    }
    return syndicate.succession;
}

export function isSuccessionArmed() {
    return getCharterRank('succession-doctrine') > 0 && !ensureSuccessionState().paused;
}

/** Reset per-tour tracking. Called at promotion and at founding. */
export function resetSuccessionTour() {
    const s = ensureSuccessionState();
    s.tourRaids = 0;
    s.peakRate = 0;
    s.peakAtRaid = 0;
}

/**
 * The marks floor a player will accept before giving up a developed character.
 *
 * Mirrors the simulator's `nextUpgradeTargetCost`: promotion has to BUY
 * something, or it is just deleting a character. The sim reads its scripted
 * UPGRADE_PLAN; in-game there is no plan, so the equivalent question is "what
 * is the cheapest thing I still cannot afford" — the next capability actually
 * within reach. Returns 0 when everything is owned, so the peak alone decides.
 *
 * Prices come from commandUpgradeCost, so Institutional Memory lowers the bar
 * to promote as well as the bar to buy.
 */
export function promotionFloor() {
    let cheapest = 0;
    for (const id of Object.keys(config.commandUpgrades || {})) {
        const cost = commandUpgradeCost(id);
        if (cost == null) continue;                       // maxed
        if (requiredUpgradesMissing(config.commandUpgrades[id]).length) continue; // not yet reachable
        if (cheapest === 0 || cost < cheapest) cheapest = cost;
    }
    return cheapest;
}

/**
 * Record a completed raid and re-evaluate the promotion peak.
 *
 * Evaluated at RAID END, not on a timer: it is the moment the marks rate
 * actually moves, and it keeps tour lengths off a wall clock.
 *
 * @returns {object} the live succession status (same shape as successionStatus)
 */
export function noteRaidCompletedForSuccession() {
    const s = ensureSuccessionState();
    s.tourRaids += 1;

    const marks = currentCharacterMarks();
    const avgRate = s.tourRaids > 0 ? marks / s.tourRaids : 0;

    // Peak tracking only starts after the minimum tour, so an early one-craft
    // spike cannot anchor a rate the rest of the tour can never beat.
    if (s.tourRaids >= SUCCESSION_POLICY.minTourRaids && avgRate > s.peakRate) {
        s.peakRate = avgRate;
        s.peakAtRaid = s.tourRaids;
    }
    return successionStatus();
}

function currentCharacterMarks() {
    if (!gameState.character) return 0;
    return calculateCommandMarks(gameState.character)?.total || 0;
}

/**
 * Whether promotion is due, and why — the ONE computation the Command panel's
 * rate line and the automation both read (docs §4.4). If these ever diverge,
 * the panel is telling the player something the automation does not believe.
 */
export function successionStatus() {
    const s = ensureSuccessionState();
    const marks = currentCharacterMarks();
    // Marks BANK across promotions, so the question is not "did this tour earn
    // enough" but "will promoting now let me buy the thing I am saving for".
    const marksIfPromotedNow = (gameState.command?.marks || 0) + marks;
    const floor = promotionFloor();
    const avgRate = s.tourRaids > 0 ? marks / s.tourRaids : 0;
    const raidsSincePeak = s.peakAtRaid > 0 ? s.tourRaids - s.peakAtRaid : 0;

    const peakPassed = s.peakAtRaid > 0
        && raidsSincePeak >= SUCCESSION_POLICY.peakPatienceRaids
        && marksIfPromotedNow >= floor;
    const safetyValve = s.tourRaids >= SUCCESSION_POLICY.maxTourRaids;

    return {
        tourRaids: s.tourRaids,
        marks,
        avgRate,
        peakRate: s.peakRate,
        raidsSincePeak,
        floor,
        marksIfPromotedNow,
        due: peakPassed || safetyValve,
        reason: !(peakPassed || safetyValve) ? null : (safetyValve ? 'max-tour' : 'marks-rate-peak'),
    };
}

/**
 * The successor's blueprint.
 *
 * Defaults to the outgoing character's own faction and skill shape, so the
 * doctrine is never blocked on the player having configured anything — an
 * automation that stops to ask a question is not automation. A stored template
 * (set by the player) wins when present.
 */
export function successionTemplate() {
    const s = ensureSuccessionState();
    if (s.template?.faction) return s.template;

    const outgoing = gameState.character;
    if (!outgoing) return null;
    return {
        faction: outgoing.faction,
        // Skill PLAN, not levels: the recruit starts at rank 1 and spends its
        // own points, so what carries is the ordering the player favoured.
        skillPlan: Object.entries(outgoing.skills || {})
            .sort((a, b) => (b[1] || 0) - (a[1] || 0))
            .map(([skillId]) => skillId),
        derived: true, // not player-authored; the panel can say so
    };
}

export function setSuccessionTemplate(template) {
    ensureSuccessionState().template = template;
}

export function setSuccessionPaused(paused) {
    ensureSuccessionState().paused = !!paused;
    return ensureSuccessionState().paused;
}

/**
 * Buy a charter rank. Clone of purchaseUpgradeCore, against Writs.
 *
 * `requires` names COMMAND upgrades (succession-doctrine needs Auto-Raid
 * Protocol AND Auto-Fabricator), checked against the live command layer rather
 * than the charter list - the dependency crosses layers on purpose.
 *
 * The check is `requiredUpgradesMissing` from command-core, the same function
 * the Command shop uses, rather than a second copy here: a charter requiring
 * ONE upgrade and a charter requiring TWO must not be able to disagree about
 * what "missing" means, and the single-id form silently ignored the second
 * entry the moment `requires` became a list.
 */
export function purchaseCharterUpgradeCore(charterId) {
    const def = config.syndicateUpgrades?.[charterId];
    if (!def) return { success: false, reason: 'unknown-charter' };

    const syndicate = ensureSyndicateState();
    const rank = getCharterRank(charterId);
    if (rank >= def.costs.length) return { success: false, reason: 'max-rank' };

    const missing = requiredUpgradesMissing(def);
    if (missing.length > 0) {
        return { success: false, reason: 'missing-requirement', requires: missing };
    }

    const cost = def.costs[rank];
    if (syndicate.writs < cost) return { success: false, reason: 'insufficient-writs' };

    syndicate.writs -= cost;
    syndicate.upgrades[charterId] = rank + 1;
    return { success: true, rank: rank + 1, cost };
}
